import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckIcon from '@mui/icons-material/Check';

import { useRegistrarPago, useUploadComprobante } from '../../hooks/usePagos';
import {
  VIAS_MANUALES,
  getViaPagoLabel,
  requiereComprobante,
} from '../../types/pago';
import type { Cupon } from '../../types/cupon';
import { formatCurrency, todayLocal } from '../../utils/format';
import { mapSupabaseError } from '../../utils/errores';

// ── Schema (factory para inyectar reglas dependientes del cupón) ──────────────

/**
 * Límite técnico real del campo `pagos.monto_pagado` (numeric(14,2)):
 *   999_999_999_999.99 (~1 billón). Para evitar errores de tipeo aceptamos
 *   hasta 999_999_999,99 ($999 millones). Si en algún momento se necesita
 *   más, ajustar acá y en el trigger SQL.
 */
const MONTO_MAX_PAGO = 999_999_999.99;

function makeSchema(cupon: Cupon) {
  return z.object({
    via_pago: z.enum(['ventanilla_banco', 'transferencia_otra_cuenta', 'cvu_pronto_pago']),
    monto_pagado: z
      .number({ invalid_type_error: 'Ingresá un monto válido' })
      .positive('El monto debe ser mayor a 0')
      .max(MONTO_MAX_PAGO, `El monto no puede superar $${MONTO_MAX_PAGO.toLocaleString('es-AR')}`),
    fecha_pago: z
      .string()
      .min(1, 'Fecha de pago requerida')
      .refine(
        (v) => v >= cupon.fecha_emision,
        `La fecha no puede ser anterior a la emisión del cupón (${cupon.fecha_emision})`,
      )
      .refine(
        (v) => v <= todayLocal(),
        'La fecha no puede ser futura',
      ),
    nro_comprobante: z
      .string()
      .min(3, 'Indicá el N° de comprobante (mínimo 3 caracteres)')
      .max(60, 'Máximo 60 caracteres'),
    medio_pago: z
      .string()
      .min(3, 'Indicá el medio (banco, cuenta, etc. — mínimo 3 caracteres)')
      .max(80, 'Máximo 80 caracteres'),
    observaciones: z.string().max(500).optional().or(z.literal('')),
  });
}

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

interface Props {
  open: boolean;
  cupon: Cupon;
  onClose: () => void;
  /** Callback opcional al registrar exitosamente. */
  onSuccess?: () => void;
}

const ACCEPT_TYPES = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

export default function RegistrarPagoDialog({ open, cupon, onClose, onSuccess }: Props) {
  const registrar = useRegistrarPago();
  const upload = useUploadComprobante();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Schema dinámico: las validaciones cruzadas (fecha) dependen del cupón
  const schema = useMemo(() => makeSchema(cupon), [cupon]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      via_pago: 'transferencia_otra_cuenta',
      monto_pagado: cupon.monto_total,
      fecha_pago: todayLocal(),
      nro_comprobante: '',
      medio_pago: '',
      observaciones: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        via_pago: 'transferencia_otra_cuenta',
        monto_pagado: cupon.monto_total,
        fecha_pago: todayLocal(),
        nro_comprobante: '',
        medio_pago: '',
        observaciones: '',
      });
      setFile(null);
      setFileError(null);
    }
  }, [open, cupon.monto_total, reset]);

  const via = watch('via_pago');
  const monto = watch('monto_pagado');
  const comprobanteObligatorio = requiereComprobante(via);
  const esPago = typeof monto === 'number' && !isNaN(monto);
  const esParcial = esPago && monto < cupon.monto_total;
  const esExcedente = esPago && monto > cupon.monto_total;

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFileError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError('El archivo supera los 10 MB.');
      return;
    }
    setFile(f);
  }

  const onSubmit = async (values: FormValues) => {
    setFileError(null);
    // Validación de comprobante: registrar deja estado='pendiente' y el comprobante
    // técnicamente puede subirse después, pero el operador siempre lo carga al
    // registrar (decisión #023 — comprobante obligatorio para vias manuales).
    if (comprobanteObligatorio && !file) {
      setFileError('Adjuntá el comprobante (obligatorio para esta vía).');
      return;
    }

    let comprobante_url: string | null = null;
    try {
      if (file) {
        const { path } = await upload.mutateAsync({
          empresaId: cupon.empresa_id,
          scope: cupon.id,
          file,
        });
        comprobante_url = path;
      }

      await registrar.mutateAsync({
        empresa_id: cupon.empresa_id,
        periodo_id: cupon.periodo_id,
        cupon_id: cupon.id,
        planilla_id: cupon.planilla_id,
        via_pago: values.via_pago,
        monto_total: cupon.monto_total,
        monto_pagado: values.monto_pagado,
        fecha_pago: values.fecha_pago,
        medio_pago: values.medio_pago || null,
        nro_comprobante: values.nro_comprobante || null,
        comprobante_url,
        observaciones: values.observaciones || null,
      });
      onSuccess?.();
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (upload.error || registrar.error) as Error | null;

  // Se conserva el error crudo en consola para diagnóstico; al usuario se le
  // muestra el mensaje traducido (ticket #490).
  useEffect(() => {
    if (apiError) {
      // eslint-disable-next-line no-console
      console.error('[RegistrarPago] error al registrar el pago:', apiError);
    }
  }, [apiError]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>Registrar pago</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info" icon={false}>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  Cupón <strong>{cupon.codigo}</strong>
                </Typography>
                <Typography variant="body2">
                  Total del cupón: <strong>{formatCurrency(cupon.monto_total)}</strong>
                </Typography>
              </Stack>
            </Alert>

            {apiError && (
              <Alert severity="error">
                {mapSupabaseError(apiError, 'No se pudo registrar el pago. Volvé a intentar; si persiste, avisá al administrador.')}
              </Alert>
            )}

            <Controller
              name="via_pago"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  required
                  fullWidth
                  label="Vía de pago"
                  error={!!errors.via_pago}
                  helperText={errors.via_pago?.message}
                >
                  {VIAS_MANUALES.map((v) => (
                    <MenuItem key={v} value={v}>
                      {getViaPagoLabel(v)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {via === 'cvu_pronto_pago' && (
              <Alert severity="warning" sx={{ fontSize: 13 }}>
                <strong>Pago CVU manual:</strong> usalo solo cuando tengas el
                comprobante del payer pero la transferencia aún no fue
                acreditada por Pronto Pago (webhook atrasado o ventana de
                consulta del día expirada). Si después llega la transacción
                por webhook, el sistema te va a avisar en la bandeja de
                Transacciones con un alert de posible duplicado — rechazar
                la trx para no duplicar el pago.
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="monto_pagado"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))
                    }
                    type="number"
                    required
                    fullWidth
                    label="Monto pagado"
                    error={!!errors.monto_pagado}
                    helperText={
                      errors.monto_pagado?.message ??
                      (esParcial
                        ? `Pago parcial: faltarán ${formatCurrency(cupon.monto_total - monto)}`
                        : esExcedente
                          ? `Excedente: ${formatCurrency(monto - cupon.monto_total)}`
                          : 'Coincide con el total del cupón')
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                )}
              />
              <TextField
                {...register('fecha_pago')}
                type="date"
                required
                fullWidth
                label="Fecha de pago"
                InputLabelProps={{ shrink: true }}
                error={!!errors.fecha_pago}
                helperText={errors.fecha_pago?.message}
              />
            </Stack>

            <TextField
              {...register('nro_comprobante')}
              required
              fullWidth
              label="N° de comprobante / transferencia"
              placeholder="Ej: 00012345"
              error={!!errors.nro_comprobante}
              helperText={
                errors.nro_comprobante?.message ??
                'Referencia única del payer (banco / billetera).'
              }
            />

            <TextField
              {...register('medio_pago')}
              required
              fullWidth
              label="Detalle del medio"
              placeholder="Ej: Banco Nación, Cuenta corriente XXX"
              error={!!errors.medio_pago}
              helperText={
                errors.medio_pago?.message ??
                'Banco / billetera / sucursal de origen.'
              }
            />

            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                fullWidth
              >
                {file ? 'Reemplazar comprobante' : 'Adjuntar comprobante'}
                <input
                  hidden
                  type="file"
                  accept={ACCEPT_TYPES}
                  onChange={handleFile}
                />
              </Button>
              {file && (
                <Chip
                  sx={{ mt: 1 }}
                  icon={<CheckIcon />}
                  label={`${file.name} (${(file.size / 1024).toFixed(0)} KB)`}
                  color="success"
                  variant="outlined"
                  onDelete={() => setFile(null)}
                />
              )}
              {!file && comprobanteObligatorio && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Comprobante obligatorio para esta vía (PDF, PNG o JPG · máx. 10 MB).
                </Typography>
              )}
              {fileError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {fileError}
                </Typography>
              )}
            </Box>

            <TextField
              {...register('observaciones')}
              fullWidth
              multiline
              rows={2}
              label="Observaciones (opcional)"
              error={!!errors.observaciones}
              helperText={errors.observaciones?.message}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting || upload.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || upload.isPending}
            startIcon={
              isSubmitting || upload.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {isSubmitting || upload.isPending
              ? 'Registrando...'
              : 'Registrar pago'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
